// 🏆 Achievement Notification Banner Component
import React, { useState, useEffect } from 'react';
import { playSound } from '../../utils/audio';

interface AchievementNotificationProps {
  achievement: {
    title: string;
    description?: string;
    iconUrl: string;
    museCashReward: number;
    rarity?: string;
  } | null;
  onComplete: () => void;
}

const AchievementNotification: React.FC<AchievementNotificationProps> = ({ 
  achievement, 
  onComplete 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  useEffect(() => {
    if (achievement) {
      // Slide in immediately
      setIsVisible(true);
      setIsAnimatingOut(false);

      // Play a one-shot reward sound based on rarity (Reward is Loud)
      try {
        const rarityKey = (achievement.rarity || 'common').toLowerCase();
        if (rarityKey === 'legendary') {
          playSound('/audio/triumph.mp3', 0.7);
        } else if (rarityKey === 'epic') {
          playSound('/audio/fireworks.mp3', 0.6);
        } else if (rarityKey === 'rare') {
          playSound('/audio/badge-drop.mp3', 0.5);
        } else {
          // common / default
          playSound('/audio/jingle-select.mp3', 0.4);
        }
      } catch (e) {
        // Fail silently if audio is unavailable
        console.warn('Failed to play achievement sound', e);
      }

      // Auto-hide after 4 seconds
      const timer = setTimeout(() => {
        handleClose();
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [achievement]);

  const handleClose = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      setIsVisible(false);
      onComplete();
    }, 300);
  };

  if (!achievement || !isVisible) return null;

  const getRarityColor = (rarity?: string) => {
    switch (rarity) {
      case 'legendary': return '#FF6B35';
      case 'epic': return '#9B59B6';
      case 'rare': return '#3498DB';
      case 'common': 
      default: return '#2ECC71';
    }
  };

  const getRarityGlow = (rarity?: string) => {
    switch (rarity) {
      case 'legendary': return '0 0 20px rgba(255, 107, 53, 0.5)';
      case 'epic': return '0 0 20px rgba(155, 89, 182, 0.5)';
      case 'rare': return '0 0 20px rgba(52, 152, 219, 0.5)';
      case 'common':
      default: return '0 0 20px rgba(46, 204, 113, 0.5)';
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: '20px',
        right: isAnimatingOut ? '-420px' : '20px', // Slide from right side
        // Must sit above games/iframes + app chrome (sidebars, overlays, etc.)
        zIndex: 2200,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '12px',
        padding: '12px 16px',
        width: '380px', // Narrower for side popup
        minHeight: '80px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        border: `2px solid ${getRarityColor(achievement.rarity)}`,
        color: 'white',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        transition: 'right 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)', // Smooth slide animation
        cursor: 'pointer',
        overflow: 'hidden'
      }}
      onClick={handleClose}
    >
        <div style={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: '12px',
          width: '100%'
        }}>
          {/* Achievement Icon */}
          <div
            style={{
              fontSize: '28px',
              background: getRarityColor(achievement.rarity),
              borderRadius: '50%',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              marginTop: '4px'
            }}
          >
            {achievement.iconUrl}
          </div>

          {/* Achievement Content */}
          <div style={{ 
            flex: 1, 
            minWidth: 0,
            paddingTop: '4px'
          }}>
            <div
              style={{
                fontSize: '11px',
                opacity: 0.9,
                marginBottom: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: '600'
              }}
            >
              🏆 Achievement Unlocked!
            </div>
            
            <div
              style={{
                fontSize: '18px',
                fontWeight: 'bold',
                marginBottom: '4px',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
                lineHeight: '1.2'
              }}
            >
              {achievement.title}
            </div>
            
            {achievement.description && (
              <div
                style={{
                  fontSize: '13px',
                  opacity: 0.85,
                  lineHeight: '1.3',
                  marginBottom: '8px'
                }}
              >
                {achievement.description}
              </div>
            )}

            {/* Muse Cash Reward - moved below content */}
            <div
              style={{
                background: 'rgba(255, 215, 0, 0.2)',
                borderRadius: '8px',
                padding: '6px 10px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                border: '1px solid rgba(255, 215, 0, 0.4)'
              }}
            >
              <span style={{ fontSize: '16px' }}>💰</span>
              <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                +{achievement.museCashReward} Muse Cash
              </span>
            </div>
          </div>
        </div>

        {/* Close Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            color: 'white',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
          }}
        >
          ×
        </button>
      </div>
  );
};

export default AchievementNotification; 